import { apiRequest } from "../services/apiRequest";

/** Shared by the JSON and CSV Subscriber-Tool-Usage calls so the two can never
 *  drift and export a different slice than the table on screen. */
function subscriberToolUsageParams({
  filterType,
  startDate,
  endDate,
  subscribers,
  plan,
}) {
  const params = new URLSearchParams();
  params.set("filterType", filterType);
  if (filterType === "customdate" && startDate && endDate) {
    params.set("startDate", startDate);
    params.set("endDate", endDate);
  }
  if (subscribers) params.set("subscribers", subscribers);
  if (plan) params.set("plan", plan);
  return params;
}

export const Analytics = {
  getSuperAdminMetrics: async () =>
    await apiRequest("get", `/api/get-merchant-count/`),

  // PostHog product-analytics overview (DAU, WAU, event totals, referring
  // domains, pageview funnel). Optional filters: days (1-365), event.
  getPostHogOverview: async ({ days, event } = {}) => {
    const params = new URLSearchParams();
    if (days) params.set("days", days);
    if (event) params.set("event", event);
    const qs = params.toString();
    return await apiRequest(
      "get",
      `/api/posthog/overview/${qs ? `?${qs}` : ""}`
    );
  },

  // Recent raw events for the PostHog Activity feed. Optional limit (1-100).
  getPostHogActivity: async ({ limit } = {}) => {
    const qs = limit ? `?limit=${limit}` : "";
    return await apiRequest("get", `/api/posthog/activity/${qs}`);
  },

  // Recent sessions for the Activity "Sessions" tab. Optional limit (1-100).
  getPostHogSessions: async ({ limit } = {}) => {
    const qs = limit ? `?limit=${limit}` : "";
    return await apiRequest("get", `/api/posthog/sessions/${qs}`);
  },

  // Conversion-rate stats (sign-in / subscription / return), % + raw counts.
  // `days` scopes the PostHog side (visitors + returners).
  getPostHogConversions: async ({ days } = {}) => {
    const qs = days ? `?days=${days}` : "";
    return await apiRequest("get", `/api/posthog/conversions/${qs}`);
  },

  // 3-step pageview funnel broken down by browser. `days` sets the window.
  getPostHogFunnel: async ({ days } = {}) => {
    const qs = days ? `?days=${days}` : "";
    return await apiRequest("get", `/api/posthog/funnel/${qs}`);
  },

  getGraph: async (storeId, params) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/analytics/${storeId}${
      queryString ? `?${queryString}` : ""
    }`;
    return await apiRequest("get", url);
  },

  getProductMetrics: async (
    storeId,
    days,
    startDate = null,
    endDate = null
  ) => {
    const queryParams = new URLSearchParams();
    queryParams.append("store_id", storeId);
    if (days) queryParams.append("days", days);
    if (startDate) queryParams.append("startDate", startDate);
    if (endDate) queryParams.append("endDate", endDate);
    return await apiRequest(
      "get",
      `/api/product-metrics?${queryParams.toString()}`
    );
  },

  getBusinessMetrics: async (year) =>
    await apiRequest("get", `/api/business-metrics/${year}`),

  // ✅ Countries uppercase and joined with '+'
  getUserPaymentSummary: async (
    startDate,
    endDate,
    filterType,
    countryList,
    page = 1
  ) => {
    const params = [];

    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    if (filterType) params.push(`filterType=${filterType}`);
    if (page) params.push(`page=${page}`);

    if (Array.isArray(countryList) && countryList.length > 0) {
      const uniqueCountries = [
        ...new Set(countryList.map((c) => c.toUpperCase())),
      ];
      params.push(`country=${uniqueCountries.join("+")}`);
    } else if (
      typeof countryList === "string" &&
      countryList &&
      countryList !== "All"
    ) {
      params.push(`country=${countryList.toUpperCase()}`);
    }

    const queryString = params.join("&");
    const url = `/account/user_payment_summery/?${queryString}`;

    return await apiRequest("get", url);
  },

  getUserRevenueGraph: async (startDate, endDate, filterType, countryList) => {
    const params = [];
    const type = filterType || "last30days";
    params.push(`filterType=${type}`);

    if (type === "customdate") {
      if (startDate) params.push(`startDate=${startDate}`);
      if (endDate) params.push(`endDate=${endDate}`);
    }

    if (Array.isArray(countryList) && countryList.length > 0) {
      const uniqueCountries = [
        ...new Set(countryList.map((c) => c.toUpperCase())),
      ];
      params.push(`country=${uniqueCountries.join("+")}`);
    } else if (
      typeof countryList === "string" &&
      countryList &&
      countryList !== "All"
    ) {
      params.push(`country=${countryList.toUpperCase()}`);
    }

    const queryString = params.join("&");
    const url = `/account/graphs/users-revenue/?${queryString}`;

    return await apiRequest("get", url);
  },

  getCountryRollup: async () => {
    return await apiRequest("get", `/account/country-rollup/`);
  },

  getUserList: async ({
    search = "",
    name = "", // legacy alias; prefer `search`
    status = "",
    plan = "",
    country = "",
    ordering = "",
    page = 1,
    exportType = "",
  }) => {
    const queryParams = new URLSearchParams();
    const searchQuery = search || name;
    if (searchQuery) queryParams.set("search", searchQuery);
    if (status) queryParams.set("status", status);
    if (plan) queryParams.set("plan", plan);
    if (country) queryParams.set("country", country);
    if (ordering) queryParams.set("ordering", ordering);
    queryParams.set("page", page);
    if (exportType) queryParams.set("export", exportType);

    return await apiRequest(
      "get",
      `/account/users-list/?${queryParams.toString()}`
    );
  },

  deleteUser: async (userId) => {
    if (!userId) throw new Error("User ID is required");
    const queryParams = new URLSearchParams();
    queryParams.set("user_id", userId);
    return await apiRequest(
      "delete",
      `/account/users-list/?${queryParams.toString()}`
    );
  },

  // ✅ NEW: Fetch user detail by ID. Optional `page` paginates the embedded
  // subscription_history list server-side (?page=N).
  getUserDetail: async (userId, page = null) => {
    if (!userId) throw new Error("User ID is required");
    const qs = page ? `?page=${page}` : "";
    const url = `/account/user_detail/${userId}/${qs}`;
    return await apiRequest("get", url);
  },

  // ✅ NEW: Fetch user timeline with filters
  getUserTimeline: async ({
    userId,
    filterType = "last90days",
    activityType = "",
    startDate = null,
    endDate = null,
    page = 1,
  }) => {
    if (!userId) throw new Error("User ID is required");

    const params = new URLSearchParams();

    // Default filter
    params.set("filterType", filterType || "last90days");

    // Optional: activity type like "Generations", "Payments"
    if (activityType && activityType !== "All Activities")
      params.set("activityType", activityType);

    // Optional: for future custom range support
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);

    // Pagination support
    params.set("page", page);

    const url = `/account/timeline_list/${userId}/?${params.toString()}`;
    return await apiRequest("get", url);
  },

  // Existing Analytics object...
  getAiFeatureDetail: async (uuid, feature = null) => {
    if (!uuid) throw new Error("UUID is required");
    const qs =
      feature && String(feature).trim() !== ""
        ? `?feature=${encodeURIComponent(String(feature).trim())}`
        : "";
    const url = `/account/ai_feature_detail/${uuid}/${qs}`;
    return await apiRequest("get", url);
  },

  // ✅ Fetch merchant stats (New API Integration)
  // ✅ Updated: Fetch merchant stats (supports custom range)
  getMerchantStats: async (
    filterType = "last30days",
    countries = "All",
    merchant = null,
    startDate = null,
    endDate = null
  ) => {
    const params = new URLSearchParams();

    // filterType (e.g., last30days, last7days, customdate)
    params.set("filterType", filterType);

    // handle custom date range if applicable
    if (filterType === "customdate" && startDate && endDate) {
      params.set("startDate", startDate);
      params.set("endDate", endDate);
    }

    // handle country (single or list) — omit when none selected
    if (Array.isArray(countries) && countries.length > 0) {
      const unique = [
        ...new Set(countries.map((c) => String(c).toUpperCase())),
      ];
      if (unique.length > 0) params.set("countries", unique.join("+"));
    } else if (
      typeof countries === "string" &&
      countries &&
      countries !== "All"
    ) {
      // normalize strings like "AE,SA" or "AE+SA" -> "AE+SA"
      const parts = countries
        .split(/[+,]/)
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean);
      const unique = [...new Set(parts)];
      if (unique.length > 0) params.set("countries", unique.join("+"));
    }

    // handle merchant IDs (single or list) - join by '+'
    // Do NOT add this param when no selection is made
    if (Array.isArray(merchant) && merchant.length > 0) {
      const ids = merchant.map((m) => String(m).trim()).filter(Boolean);
      const uniqueMerchant = [...new Set(ids)];
      if (uniqueMerchant.length > 0) {
        params.set("merchant", uniqueMerchant.join("+"));
      }
    } else if (typeof merchant === "string" && merchant && merchant !== "All") {
      const parts = merchant
        .split(/[+,]/)
        .map((m) => String(m).trim())
        .filter(Boolean);
      const unique = [...new Set(parts)];
      if (unique.length > 0) params.set("merchant", unique.join("+"));
    } else if (typeof merchant === "number") {
      // Handle single ID as number
      params.set("merchant", String(merchant));
    }

    const queryString = params.toString().replace(/%2B/g, "+");
    const url = `/account/merchant_stats/?${queryString}`;
    return await apiRequest("get", url);
  },

  // ✅ NEW: Fetch merchant analytics
  getMerchantAnalytics: async (
    filterType = "last30days",
    countries = "All",
    startDate = null,
    endDate = null
  ) => {
    const params = new URLSearchParams();
    params.set("filterType", filterType);

    if (filterType === "customdate" && startDate && endDate) {
      params.set("startDate", startDate);
      params.set("endDate", endDate);
    }

    // Countries normalization (omit when none selected)
    if (Array.isArray(countries) && countries.length > 0) {
      const unique = [
        ...new Set(countries.map((c) => String(c).toUpperCase())),
      ];
      if (unique.length > 0) params.set("countries", unique.join("+"));
    } else if (
      typeof countries === "string" &&
      countries &&
      countries !== "All"
    ) {
      const parts = countries
        .split(/[+,]/)
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean);
      const unique = [...new Set(parts)];
      if (unique.length > 0) params.set("countries", unique.join("+"));
    }

    const queryString = params.toString().replace(/%2B/g, "+");
    const url = `/account/merchant_analytics/?${queryString}`;
    return await apiRequest("get", url);
  },

  getStoreAnalytics: async (
    filterType = "last30days",
    countries = "All",
    startDate = null,
    endDate = null
  ) => {
    const params = new URLSearchParams();
    params.set("filterType", filterType);
    if (filterType === "customdate" && startDate && endDate) {
      params.set("startDate", startDate);
      params.set("endDate", endDate);
    }
    // Countries normalization (omit when none selected)
    if (Array.isArray(countries) && countries.length > 0) {
      const unique = [
        ...new Set(countries.map((c) => String(c).toUpperCase())),
      ];
      if (unique.length > 0) params.set("countries", unique.join("+"));
    } else if (
      typeof countries === "string" &&
      countries &&
      countries !== "All"
    ) {
      const parts = countries
        .split(/[+,]/)
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean);
      const unique = [...new Set(parts)];
      if (unique.length > 0) params.set("countries", unique.join("+"));
    }
    const queryString = params.toString().replace(/%2B/g, "+");
    const url = `/account/store_analytics/?${queryString}`;
    return await apiRequest("get", url);
  },

  // ✅ NEW: Fetch Product Analytics
  getProductAnalytics: async (
    filterType = "last30days",
    countries = "All",
    startDate = null,
    endDate = null
  ) => {
    const params = new URLSearchParams();
    params.set("filterType", filterType);

    if (filterType === "customdate" && startDate && endDate) {
      params.set("startDate", startDate);
      params.set("endDate", endDate);
    }

    // Countries normalization (omit when none selected)
    if (Array.isArray(countries) && countries.length > 0) {
      const unique = [
        ...new Set(countries.map((c) => String(c).toUpperCase())),
      ];
      if (unique.length > 0) params.set("countries", unique.join("+"));
    } else if (
      typeof countries === "string" &&
      countries &&
      countries !== "All"
    ) {
      const parts = countries
        .split(/[+,]/)
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean);
      const unique = [...new Set(parts)];
      if (unique.length > 0) params.set("countries", unique.join("+"));
    }

    const queryString = params.toString().replace(/%2B/g, "+");
    const url = `/account/product_analytics/?${queryString}`;
    return await apiRequest("get", url);
  },

  getSallaAnalytics: async (filterType = "last30days", startDate = null, endDate = null, storeId = null) => {
    const params = new URLSearchParams();
    params.set("filterType", filterType);
    if (filterType === "customdate" && startDate && endDate) {
      params.set("startDate", startDate);
      params.set("endDate", endDate);
    }
    if (storeId) params.set("store_id", storeId);
    return await apiRequest("get", `/api/salla/analytics/list/?${params.toString()}`);
  },

  // Plan Usage Statistics — average images / furniture-finder consumption per
  // subscriber, per plan, against what each plan includes.
  // filterType: alltime | last7days | last15days | last30days | last90days |
  //             customdate (startDate + endDate required, YYYY-MM-DD).
  getPlanUsageStats: async ({
    filterType = "last30days",
    startDate = null,
    endDate = null,
  } = {}) => {
    const params = new URLSearchParams();
    params.set("filterType", filterType);
    if (filterType === "customdate" && startDate && endDate) {
      params.set("startDate", startDate);
      params.set("endDate", endDate);
    }
    return await apiRequest(
      "get",
      `/account/plan_usage_stats/?${params.toString()}`
    );
  },

  // Same data as a CSV attachment. Returns the raw axios response so the
  // caller can read the filename off content-disposition.
  exportPlanUsageStats: async ({
    filterType = "last30days",
    startDate = null,
    endDate = null,
  } = {}) => {
    const params = new URLSearchParams();
    params.set("filterType", filterType);
    if (filterType === "customdate" && startDate && endDate) {
      params.set("startDate", startDate);
      params.set("endDate", endDate);
    }
    params.set("export", "csv");
    return await apiRequest(
      "get",
      `/account/plan_usage_stats/?${params.toString()}`,
      undefined,
      { skipCache: true, responseType: "blob" }
    );
  },

  // Subscriber Generation by Tool — the second tab of the Plan Usage page.
  // Same window vocabulary as getPlanUsageStats, plus:
  //   subscribers: all | paid | trial
  //   plan:        a plan id, or "all"
  getSubscriberToolUsage: async ({
    filterType = "last30days",
    startDate = null,
    endDate = null,
    subscribers = "all",
    plan = "all",
  } = {}) => {
    const params = subscriberToolUsageParams({
      filterType,
      startDate,
      endDate,
      subscribers,
      plan,
    });
    return await apiRequest(
      "get",
      `/account/subscriber_tool_usage/?${params.toString()}`
    );
  },

  // Same data as a CSV attachment. Returns the raw axios response so the caller
  // can read the filename off content-disposition.
  exportSubscriberToolUsage: async ({
    filterType = "last30days",
    startDate = null,
    endDate = null,
    subscribers = "all",
    plan = "all",
  } = {}) => {
    const params = subscriberToolUsageParams({
      filterType,
      startDate,
      endDate,
      subscribers,
      plan,
    });
    params.set("export", "csv");
    return await apiRequest(
      "get",
      `/account/subscriber_tool_usage/?${params.toString()}`,
      undefined,
      { skipCache: true, responseType: "blob" }
    );
  },

  // ✅ Fetch furniture JSON data by image key
  getFurnitureJson: async (key) => {
    if (!key) throw new Error("Image key is required");
    // Preserve "/" in the key instead of encoding to "%2F"
    const encodedKey = encodeURIComponent(key).replace(/%2F/g, "/");
    const url = `/account/get_furniture_json/?key=${encodedKey}`;
    return await apiRequest("get", url);
  },
};
